import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Btn, Textarea, BackButton, RNLLoader } from '../UI';
import * as api from '../../api';
import { parsePvErrorMessage } from './pvUtils';
import PaymentVoucherForm from './PaymentVoucherForm';
import VoucherApprovalHistory from './VoucherApprovalHistory';
import VoucherOutcomeModal from './VoucherOutcomeModal';

const NAVY = '#0b318b';
const SEC = { fontSize: 12, fontWeight: 800, color: NAVY, letterSpacing: 0.4, marginBottom: 10 };

// Header fields worth reporting in the change summary. Anything not listed
// still saves — this is what a reviewer would want called out, not an
// exhaustive dump of every key in the payload.
const TRACKED = [
  ['narration', 'Narration'],
  ['payment_method', 'Payment Method'],
  ['payment_type', 'Payment Type'],
  ['priority', 'Priority'],
  ['manual_voucher_no', 'Manual Voucher No.'],
  ['invoice_no', 'Invoice No.'],
  ['invoice_date', 'Invoice Date'],
  ['delivery_note_no', 'Delivery Note'],
  ['approval_memo_no', 'Approval Memo'],
  ['po_number', 'Purchase Order'],
  ['grn_number', 'GRN'],
  ['less_discount', 'Discount'],
  ['less_retention', 'Retention'],
  ['other_additions', 'Other Additions'],
  ['withheld_vat', 'Withheld VAT'],
  ['other_deductions', 'Other Deductions'],
  ['vat_percent', 'VAT %'],
  ['wht_percent', 'WHT %'],
  ['subtotal_amount', 'Subtotal'],
  ['net_payable', 'Net Payable'],
];

const show = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const same = (a, b) => show(a) === show(b) || Number(a || 0) === Number(b || 0);

/**
 * Answering a return.
 *
 * Deliberately its own screen rather than the voucher form with a confirm
 * dialog bolted on: correcting a returned voucher is a different task from
 * raising one. The thing being answered — the reason it came back — has to be
 * the first thing on the page, and the person correcting it should see what
 * they are about to change before it goes.
 */
export default function VoucherResubmission({ voucherId, onBack, onDone, toast }) {
  const [voucher, setVoucher] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [backendError, setBackendError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  // The form publishes its live payload here on every edit. A ref, not state —
  // storing it would re-render the form, which would publish again, forever.
  const formState = useRef({ getPayload: null, issues: [] });
  const [issueCount, setIssueCount] = useState(0);
  const [, forceTick] = useState(0);

  const onFormState = useCallback((s) => {
    formState.current = s;
    setIssueCount(s.issues?.length || 0);
    forceTick((n) => n + 1);   // recompute the change summary
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const v = await api.getPaymentVoucher(voucherId);
        let t = null;
        if (v.workflow_instance_id) {
          try { t = await api.getWorkflowTracking(v.workflow_instance_id); } catch { /* supplementary */ }
        }
        if (!cancelled) { setVoucher(v); setTracking(t); }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [voucherId]);

  // Who returned it and why — the same rule the history timeline uses.
  const returnedStep = useMemo(
    () => [...(tracking?.approval_history || [])].reverse().find((h) => h.status === 'returned') || null,
    [tracking],
  );
  const returnReason = tracking?.return_reason || returnedStep?.comments || null;

  // Original vs the form's current payload.
  const changes = useMemo(() => {
    const getPayload = formState.current.getPayload;
    if (!voucher || !getPayload) return [];
    let p;
    try { p = getPayload(); } catch { return []; }
    const out = [];

    TRACKED.forEach(([key, label]) => {
      if (!same(voucher[key], p[key])) {
        out.push({ label, from: voucher[key], to: p[key] });
      }
    });

    // Lines are matched on their requisition line, not position — reordering
    // is not a change, and a removed line should read as removed.
    const before = voucher.items || [];
    const after = p.items || [];
    const keyOf = (i) => i.purchase_request_item_id || i.description;
    const afterByKey = new Map(after.map((i) => [keyOf(i), i]));

    before.forEach((b, idx) => {
      const a = afterByKey.get(keyOf(b));
      const n = `Line ${idx + 1} · ${(b.description || '').slice(0, 28)}`;
      if (!a) { out.push({ label: `${n}`, from: 'present', to: 'removed' }); return; }
      if (!same(b.process_quantity, a.process_quantity)) out.push({ label: `${n} · qty`, from: b.process_quantity, to: a.process_quantity });
      if (!same(b.unit_rate, a.unit_rate)) out.push({ label: `${n} · rate`, from: b.unit_rate, to: a.unit_rate });
    });
    const beforeKeys = new Set(before.map(keyOf));
    after.forEach((a) => {
      if (!beforeKeys.has(keyOf(a))) out.push({ label: `New line · ${(a.description || '').slice(0, 28)}`, from: '—', to: 'added' });
    });
    return out;
  }, [voucher, issueCount, formState.current.getPayload]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResubmit = async () => {
    setBackendError(null);
    if (!note.trim()) {
      setBackendError({ header: 'Describe what you corrected before resubmitting.', bullets: [] });
      return;
    }
    const { getPayload, issues } = formState.current;
    if (issues?.length) {
      setBackendError({ header: `Fix ${issues.length} issue${issues.length === 1 ? '' : 's'} before resubmitting:`, bullets: issues });
      return;
    }
    setSending(true);
    try {
      // Save first, then resend. If the save fails the voucher stays put and
      // nothing has been sent to an approver.
      await api.updatePaymentVoucher(voucherId, getPayload());
      await api.resubmitWorkflow(voucher.workflow_instance_id, note.trim());
      setOutcome({
        type: 'submit',
        title: 'Voucher Resubmitted',
        message: `Voucher ${voucher.voucher_no} has been corrected and sent back for approval.`,
      });
    } catch (e) {
      setBackendError(parsePvErrorMessage(e.message));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}><RNLLoader title="Loading voucher…" /></div>;
  if (error) return <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>{error}</div>;
  if (!voucher) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <BackButton onClick={onBack} style={{ marginBottom: 12 }}>← Back</BackButton>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>
          Correct &amp; Resubmit — {voucher.voucher_no}
        </h2>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
          Make the correction that was asked for, say what you changed, and send it back for approval.
        </div>
      </div>

      {/* ── 1. What is being answered ── */}
      <Card style={{ border: '1px solid #fbbf24', background: '#fffdf5', padding: 16 }}>
        <div style={{ ...SEC, color: '#b45309' }}>RETURNED FOR CORRECTION</div>
        <div style={{ fontSize: 14, fontStyle: 'italic', fontWeight: 700, color: '#7c2d12', marginBottom: 10 }}>
          {returnReason ? `“${returnReason}”` : 'No reason was recorded.'}
        </div>
        <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', fontSize: 11.5, color: '#92400e' }}>
          <span>Returned by <strong>{returnedStep?.approver_fullname || '—'}</strong></span>
          <span>At step <strong>{returnedStep?.step_name || '—'}</strong></span>
          <span>On <strong>{(returnedStep?.date_actioned || '').slice(0, 16).replace('T', ' ') || '—'}</strong></span>
        </div>
      </Card>

      {/* ── 2. The voucher, fully editable ── */}
      <PaymentVoucherForm
        existingVoucher={voucher}
        toast={toast}
        onBack={onBack}
        resubmitMode
        onFormState={onFormState}
      />

      {/* ── 3. What this resubmission changes ── */}
      <Card style={{ border: '1px solid black', padding: 16 }}>
        <div style={SEC}>WHAT YOU ARE CHANGING</div>
        {changes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Nothing has been changed yet. You can still resubmit if the correction was
            only an explanation, but normally something above should differ.
          </div>
        ) : (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <tbody>
              {changes.map((c, i) => (
                <tr key={i}>
                  <td style={{ padding: '5px 12px 5px 0', fontSize: 11.5, fontWeight: 700, color: '#475569', whiteSpace: 'nowrap' }}>{c.label}</td>
                  <td style={{ padding: '5px 10px', fontSize: 11.5, color: '#94a3b8', textDecoration: 'line-through' }}>{show(c.from)}</td>
                  <td style={{ padding: '5px 4px', fontSize: 11.5, color: '#94a3b8' }}>→</td>
                  <td style={{ padding: '5px 10px', fontSize: 11.5, fontWeight: 800, color: '#0f172a' }}>{show(c.to)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* ── 4. The reply ── */}
      <Card style={{ border: '1px solid black', padding: 16 }}>
        <div style={SEC}>WHAT YOU CORRECTED <span style={{ color: 'var(--red)' }}>*</span></div>
        <Textarea
          value={note}
          onChange={setNote}
          rows={3}
          placeholder="Describe the correction — this goes to the approver with the voucher…"
          style={(note || '').trim() ? undefined : { border: '1px solid var(--red)' }}
        />
      </Card>

      {/* ── 5. The trail so far ── */}
      <Card style={{ border: '1px solid black', padding: 16 }}>
        <div style={SEC}>APPROVAL HISTORY</div>
        <VoucherApprovalHistory voucher={voucher} tracking={tracking} />
      </Card>

      {backendError && (
        <Card style={{ border: '1px solid #fecaca', background: '#fef2f2', padding: 14 }}>
          <div style={{ fontWeight: 800, color: '#b91c1c', fontSize: 13 }}>{backendError.header}</div>
          {backendError.bullets?.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#b91c1c', fontSize: 12, lineHeight: 1.7 }}>
              {backendError.bullets.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          )}
        </Card>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <Btn variant="ghost" disabled={sending} onClick={onBack}>Cancel</Btn>
        <Btn loading={sending} onClick={handleResubmit}>
          {sending ? 'Resubmitting…' : `Resubmit to ${returnedStep?.step_name || 'Approval'}`}
        </Btn>
      </div>

      {outcome && (
        <VoucherOutcomeModal
          outcome={outcome}
          onClose={() => { setOutcome(null); onDone ? onDone() : onBack?.(); }}
        />
      )}
    </div>
  );
}
